import React, { useEffect, useState } from "react";
import { bool, shape, arrayOf } from "prop-types";
import { push, replace } from "connected-react-router";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import * as i18nUtils from "util/i18n";
import { connect, useDispatch } from "react-redux";
import { selectors as environmentSelectors, actions as environmentActions } from "reducers/environment";
import { actions as modalActions } from "reducers/modal";
import MainContainer from "pages/_components/MainContainer";
import Head from "pages/_components/Head";
import Button from "pages/_components/Button";
import I18n from "pages/_components/I18n";
import ErrorPopup from "pages/_components/ErrorPopup";
import HeaderSimple from "pages/_components/HeaderSimple/HeaderSimple";
import CustomData from "pages/_components/CustomData/CustomData";
import Checkbox from "pages/_components/Checkbox";
import Selector from "pages/_components/fields/formik/Selector";
import { isIOS } from "react-device-detect";
import EnvironmentStepper from "../_components/EnvironmentStepper";
import EnvironmentHelpSlide from "../_components/EnvironmentHelpSlide";
import Uploader from "../_components/Uploader";
import { useTaxSelector, useTaxThubanDocuments } from "./hooks";
import { useHistoricData } from "../hooks/useHistoricData";

const FORM_ID = "environment.step.taxData";
const PAGE_ID = "StepTaxData";

const StepTaxData = ({ isMobile, taxDataBackup, taxDataRequest }) => {
    const dispatch = useDispatch();
    const [error, setError] = useState("");
    const [helpIsVisible, setHelpIsVisible] = useState(false);
    const {
        thubanCodeDefault,
        thubanCodeGanancias,
        thubanCodeOtrosCertificados,
        thubanCodeIIBBlocal,
        thubanCodeIIBBMultilateral,
        thubanCodeIIBBExento,
        thubanCodeOtrosCertificadosIIBBLocal,
        thubanCodeOtrosCertificadosIIBBMult,
        thubanCodeOtrosCertificadosIIBBExento,
        thubanCodeExentoImpSello,
        thubanCodeIvaNoRetencion,
    } = useTaxThubanDocuments();

    const { status: taxDataRequestStatus, error: taxDataRequestError } = taxDataRequest;

    const { taxSelectorRequestValue, taxSelectorRequestStatus, isLegalRepresentative } = useTaxSelector();

    const isLoading = [taxSelectorRequestStatus, taxDataRequestStatus].includes("FETCHING");

    const noBackupData = taxDataBackup?.taxStamp === undefined;
    const { getBackupDataByStepId } = useHistoricData(!noBackupData);

    useEffect(() => {
        if (taxDataRequestStatus === "FAILURE") {
            dispatch(push("/environment/stepError"));
            setError(taxDataRequestError);
            dispatch(modalActions.modalShow());
        } else if (taxDataRequestStatus === "SUCCESS") {
            dispatch(environmentActions.setTaxDataRequest({ status: "INITIAL", value: "", error: null }));
            if (taxSelectorRequestValue?.isLegalRepresentative || isLegalRepresentative) {
                getBackupDataByStepId("stepShareholders");
                dispatch(push("/environment/stepShareholders"));
            } else {
                getBackupDataByStepId("stepLegalRepresentative");
                dispatch(push("/environment/stepLegalRepresentative"));
            }
        }
    }, [taxDataRequestStatus, taxDataRequestError]);

    const submitIsAllowed = ({ values }) => {
        // const { taxData } = values;

        // const firstFieldsAreValid = !!taxData;
        const firstFieldsAreValid = true || values;

        return firstFieldsAreValid;
    };

    const requiredString = i18nUtils.get("forms.inputFile.massivePayments.invalid.required");

    const formikProps = {
        validateOnChange: false,
        validateOnBlur: false,
        initialValues: {
            taxStamp: taxDataBackup?.taxStamp || taxSelectorRequestValue?.taxStamp,
            taxProfitExemptionDocumentId0: taxDataBackup?.taxProfitExemptionDocumentId0,
            taxProfitExemptionDocumentId0Name: taxDataBackup?.taxProfitExemptionDocumentId0Name,
            taxProfitRetentionDocumentId0: taxDataBackup?.taxProfitRetentionDocumentId0,
            taxProfitRetentionDocumentId0Name: taxDataBackup?.taxProfitRetentionDocumentId0Name,
            taxVatRetentionDocumentId0: taxDataBackup?.taxVatRetentionDocumentId0,
            taxVatRetentionDocumentId0Name: taxDataBackup?.taxVatRetentionDocumentId0Name,
            taxVatExemptionDocumentId0: taxDataBackup?.taxVatExemptionDocumentId0,
            taxVatExemptionDocumentId0Name: taxDataBackup?.taxVatExemptionDocumentId0Name,
            taxIIBBCategory: taxDataBackup?.taxIIBBCategory || taxSelectorRequestValue?.taxIIBBCategory || "INSCRIPTO",
            taxIIBBEnrolledCategory:
                taxDataBackup?.taxIIBBEnrolledCategory ||
                taxSelectorRequestValue?.taxIIBBEnrolledCategory ||
                "MULTILATERAL",
            taxIIBBDocumentId: taxDataBackup?.taxIIBBDocumentId || taxSelectorRequestValue?.taxIIBBDocumentId,
            taxIIBBDocumentIdName:
                taxDataBackup?.taxIIBBDocumentIdName || taxSelectorRequestValue?.taxIIBBDocumentIdName,
            taxIIBBOtherDocumentId:
                taxDataBackup?.taxIIBBOtherDocumentId || taxSelectorRequestValue?.taxIIBBOtherDocumentId,
            taxIIBBOtherDocumentIdName:
                taxDataBackup?.taxIIBBOtherDocumentIdName || taxSelectorRequestValue?.taxIIBBOtherDocumentIdName,
            taxStampDocumentId: taxDataBackup?.taxStampDocumentId || taxSelectorRequestValue?.taxStampDocumentId,
            taxStampDocumentIdName:
                taxDataBackup?.taxStampDocumentIdName || taxSelectorRequestValue?.taxStampDocumentIdName,
        },
        validationSchema: () =>
            Yup.object().shape({
                dateFromCertMiPyme: Yup.string(),
                dateToCertMiPyme: Yup.string(),
                taxIIBBCategory: Yup.string().required(requiredString),
                taxIIBBEnrolledCategory: Yup.string().when("taxIIBBCategory", {
                    is: "INSCRIPTO",
                    then: Yup.string().required(requiredString),
                }),
                taxIIBBDocumentId: Yup.string().required(requiredString),
                taxIIBBOtherDocumentId: Yup.string().notRequired(),
                taxStamp: Yup.bool(),
                taxStampDocumentId: Yup.string().when("taxStamp", {
                    is: true,
                    then: Yup.string().required(requiredString),
                }),
            }),
        onSubmit: (values) => {
            if (values) {
                const saveValues = {
                    certificateMiPyme: taxSelectorRequestValue?.certificateMiPyme,
                    registeredResponsible: taxSelectorRequestValue?.registeredResponsible,
                    vatRegistered: taxSelectorRequestValue?.vatRegistered,
                    incomeTaxRegistered: taxSelectorRequestValue?.incomeTaxRegistered,
                    taxProfitList: taxSelectorRequestValue?.taxProfitList,
                    taxVatGralList: taxSelectorRequestValue?.taxVatGralList,
                    taxVatList: taxSelectorRequestValue?.taxVatList,
                    taxStamp: values?.taxStamp,
                    taxIIBBCategory: values?.taxIIBBCategory,
                    taxIIBBEnrolledCategory: values?.taxIIBBEnrolledCategory,
                    taxIIBBDocumentId: values?.taxIIBBDocumentId,
                    taxIIBBDocumentIdName: values?.taxIIBBDocumentIdName,
                    taxProfitExemptionDocumentId0: values?.taxProfitExemptionDocumentId0,
                    taxProfitExemptionDocumentId0Name: values?.taxProfitExemptionDocumentId0Name,
                    taxProfitRetentionDocumentId0: values?.taxProfitRetentionDocumentId0,
                    taxProfitRetentionDocumentId0Name: values?.taxProfitRetentionDocumentId0Name,
                    taxVatRetentionDocumentId0: values?.taxVatRetentionDocumentId0,
                    taxVatRetentionDocumentId0Name: values?.taxVatRetentionDocumentId0Name,
                    taxVatExemptionDocumentId0: values?.taxVatExemptionDocumentId0,
                    taxVatExemptionDocumentId0Name: values?.taxVatExemptionDocumentId0Name,
                    taxIIBBOtherDocumentId: values?.taxIIBBOtherDocumentId,
                    taxIIBBOtherDocumentIdName: values?.taxIIBBOtherDocumentIdName,
                    taxStampDocumentId: values?.taxStampDocumentId,
                    taxStampDocumentIdName: values?.taxStampDocumentIdName,
                };

                dispatch(environmentActions.setTaxDataBackup(saveValues));
                dispatch(environmentActions.saveTax(saveValues));
            }
        },
    };

    const onHeaderBack = () => {
        dispatch(replace("/environment/stepGeneralData"));
        getBackupDataByStepId("stepGeneralData");
    };

    return (
        <>
            {isMobile ? (
                <Head
                    titleText={i18nUtils.get("legalPerson.title", "Registro")}
                    onBack={onHeaderBack}
                    hideNavbarInMobile
                    tooltipMobile
                    onClickTooltip={() => setHelpIsVisible(true)}
                    tooltipImage={isIOS ? "images/figma/icon/help-white.svg" : "images/figma/icon/help.svg"}
                    showCenterContent
                />
            ) : (
                <HeaderSimple inverse />
            )}
            <MainContainer className="onboarding" showLoader={isLoading}>
                <EnvironmentStepper isMobile={isMobile} page="StepTaxData" />
                <div className="step step-general-data step-tax-data">
                    {!isMobile && (
                        <Head
                            titleText={i18nUtils.get("", "Datos impositivos")}
                            onBack={onHeaderBack}
                            tooltip
                            showCenterContent
                            onClickTooltip={() => setHelpIsVisible(true)}
                            tooltipImage="images/figma/icon/help.svg"
                        />
                    )}
                    {isMobile && (
                        <I18n id="environment.taxData.title" defaultValue="Datos impositivos" component="h1" />
                    )}
                    <Formik {...formikProps}>
                        {(allFormik) => (
                            <Form>
                                <div className="grid-table grid-table-form">
                                    <div className="grid-table-row grid-cols-12">
                                        <div className="grid-table-data col-start-2 col-span-10 subtitle">
                                            <I18n id="" defaultValue="Certificado MiPyme" component="h2" />
                                        </div>
                                        {/*
                                        {!taxSelectorRequestValue?.certificateMiPyme?.myPyme && (
                                            <>
                                                <div className="grid-table-data col-start-2 col-span-10">
                                                    <I18n
                                                        id="environment.taxData.certificateMiPyme.empty"
                                                        defaultValue="No hay datos de certificado MiPyme"
                                                    />
                                                </div>
                                            </>
                                        )}
                                        */}
                                    </div>
                                    {taxSelectorRequestValue?.certificateMiPyme?.myPyme && (
                                        <div className="grid-table-row grid-cols-12">
                                            <div
                                                className={
                                                    isMobile
                                                        ? " box box-shadow w-full date-range-horizontal"
                                                        : "grid-table-row grid-cols-12 col-span-10 col-start-2 box box-shadow "
                                                }>
                                                <div className="grid-table-data col-span-5">
                                                    <CustomData
                                                        titleText={i18nUtils.get("", "Desde")}
                                                        titleTextType="TEXT"
                                                        valueText={taxSelectorRequestValue?.certificateMiPyme?.dateFrom}
                                                        valueTextType="TEXT"
                                                        dataClass="step-tax-data__dates"
                                                    />
                                                </div>

                                                <div className="grid-table-data col-start-7 col-span-5">
                                                    <CustomData
                                                        titleText={i18nUtils.get("", "Hasta")}
                                                        titleTextType="TEXT"
                                                        valueText={taxSelectorRequestValue?.certificateMiPyme?.dateTo}
                                                        valueTextType="TEXT"
                                                        dataClass="step-tax-data__dates"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <br />
                                    <div className="grid-table-row grid-cols-12">
                                        {/* // White card */}
                                        <div
                                            className={
                                                !isMobile
                                                    ? "grid-table-data col-start-2 col-span-10"
                                                    : "flex flex-grow w-full"
                                            }>
                                            <div className="flex flex-grow justify-between">
                                                <I18n
                                                    id=""
                                                    defaultValue="Impuesto al valor agregado (IVA)"
                                                    component="b"
                                                />
                                                {taxSelectorRequestValue?.vatRegistered && (
                                                    <I18n
                                                        id=""
                                                        defaultValue="Inscripto"
                                                        componentProps={{ className: "tag tag-success" }}
                                                    />
                                                )}
                                                {!taxSelectorRequestValue?.vatRegistered && (
                                                    <I18n
                                                        id=""
                                                        defaultValue="Exento"
                                                        componentProps={{ className: "tag tag-success" }}
                                                        component="div"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        {taxSelectorRequestValue?.taxVatList && (
                                            <div className="grid-table-data col-start-2 col-span-10">
                                                <I18n
                                                    id=""
                                                    defaultValue="Certificado de exclusión, retención y percepción del IVA "
                                                />
                                            </div>
                                        )}
                                        {taxSelectorRequestValue?.taxVatList?.map((tax) => (
                                            <div className="box box-shadow w-full grid md:col-start-2 md:col-span-10 gap-y-2">
                                                <div className="grid-table-data col-start-1 col-span-2">
                                                    <CustomData
                                                        titleText={i18nUtils.get("", "Porcentaje de exclusión")}
                                                        titleTextType="TEXT"
                                                        valueText={tax.exceptionRate}
                                                        valueTextType="TEXT"
                                                    />
                                                </div>
                                                <div className="grid-table-data col-start-2 col-span-5">
                                                    <CustomData
                                                        titleText={i18nUtils.get("", "Desde")}
                                                        titleTextType="TEXT"
                                                        valueText={tax.dateFrom}
                                                        valueTextType="TEXT"
                                                    />
                                                </div>

                                                <div className="grid-table-data col-start-7 col-span-5">
                                                    <CustomData
                                                        titleText={i18nUtils.get("", "Hasta")}
                                                        titleTextType="TEXT"
                                                        valueText={tax.dateTo}
                                                        valueTextType="TEXT"
                                                    />
                                                </div>
                                                {/*
                                                <div className="grid-table-data col-start-7 col-span-5">
                                                    {tax.vatExemptionEnabled && (
                                                        <Field
                                                            component={Uploader}
                                                            idForm={FORM_ID}
                                                            name={`taxVatExemptionDocumentId${index}`}
                                                            label={i18nUtils.get(
                                                                "",
                                                                "Adjuntá certificado de exento (opcional)",
                                                            )}
                                                            documentCode={thubanCodeIvaNoRetencion}
                                                        />
                                                    )}
                                                </div>
                                                */}
                                            </div>
                                        ))}
                                        {taxSelectorRequestValue?.taxVatGralList?.map((tax) => (
                                            <div className="box box-shadow w-full grid md:col-start-2 md:col-span-10 gap-y-2">
                                                <div className="grid-table-data col-start-1 col-span-2">
                                                    <CustomData
                                                        titleText={i18nUtils.get("", "Porcentaje de exclusión")}
                                                        titleTextType="TEXT"
                                                        valueText={tax.exceptionRate}
                                                        valueTextType="TEXT"
                                                    />
                                                </div>
                                                <div className="grid-table-data col-start-2 col-span-5">
                                                    <CustomData
                                                        titleText={i18nUtils.get("", "Desde")}
                                                        titleTextType="TEXT"
                                                        valueText={tax.dateFrom}
                                                        valueTextType="TEXT"
                                                    />
                                                </div>

                                                <div className="grid-table-data col-start-7 col-span-5">
                                                    <CustomData
                                                        titleText={i18nUtils.get("", "Hasta")}
                                                        titleTextType="TEXT"
                                                        valueText={tax.dateTo}
                                                        valueTextType="TEXT"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                        <div className="grid-table-data col-start-2 col-span-5">
                                            <Field
                                                component={Uploader}
                                                idForm={FORM_ID}
                                                name="taxVatRetentionDocumentId0"
                                                label={i18nUtils.get(
                                                    "",
                                                    "En caso de no coincidir, adjuntá  certificado de exclusión retención/percepción (opcional)",
                                                )}
                                                documentCode={thubanCodeIvaNoRetencion}
                                                defaultDocument={{
                                                    documentId: taxDataBackup?.taxVatRetentionDocumentId0,
                                                    documentName: taxDataBackup?.taxVatRetentionDocumentId0Name,
                                                }}
                                                withDocumentName
                                            />
                                        </div>
                                        <div className="grid-table-data col-start-7 col-span-5">
                                            <Field
                                                component={Uploader}
                                                idForm={FORM_ID}
                                                name="taxVatExemptionDocumentId0"
                                                label={i18nUtils.get(
                                                    "",
                                                    "En caso de no coincidir, adjuntá  certificado de exclusión retención/percepción (opcional)",
                                                )}
                                                documentCode={thubanCodeDefault}
                                                defaultDocument={{
                                                    documentId: taxDataBackup?.taxVatExemptionDocumentId0,
                                                    documentName: taxDataBackup?.taxVatExemptionDocumentId0Name,
                                                }}
                                                withDocumentName
                                            />
                                        </div>
                                    </div>
                                    <br />
                                    <div className="grid-table-row grid-cols-12">
                                        {/* // White card */}
                                        <div
                                            className={
                                                !isMobile
                                                    ? "grid-table-data col-start-2 col-span-10"
                                                    : "flex flex-grow w-full"
                                            }>
                                            <div className="flex flex-grow justify-between">
                                                <I18n id="" defaultValue="Impuestos a las Ganancias" component="b" />
                                                {taxSelectorRequestValue?.incomeTaxRegistered && (
                                                    <I18n
                                                        id=""
                                                        defaultValue="Inscripto"
                                                        componentProps={{ className: "tag tag-success" }}
                                                    />
                                                )}
                                                {!taxSelectorRequestValue?.incomeTaxRegistered && (
                                                    <I18n
                                                        id=""
                                                        defaultValue="Exento"
                                                        componentProps={{ className: "tag tag-success" }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                        {taxSelectorRequestValue?.taxProfitList && (
                                            <div className="grid-table-data col-start-2 col-span-10">
                                                <I18n
                                                    id=""
                                                    defaultValue="Certificado de exclusión, retención y percepción de ganancias"
                                                />
                                            </div>
                                        )}
                                        {taxSelectorRequestValue?.taxProfitList?.map((tax) => (
                                            <div className="box box-shadow w-full grid md:col-start-2 md:col-span-10 gap-y-2">
                                                <div className="grid-table-data col-start-1 col-span-2">
                                                    <CustomData
                                                        titleText={i18nUtils.get("", "Porcentaje de exclusión")}
                                                        titleTextType="TEXT"
                                                        valueText={tax.exceptionRate}
                                                        valueTextType="TEXT"
                                                    />
                                                </div>
                                                {/*
                                                <div className="grid-table-data col-start-7 col-span-5">
                                                    <CustomData
                                                        titleText={i18nUtils.get("", "Nro. Certificado")}
                                                        titleTextType="TEXT"
                                                        valueText={tax.certificateNumber}
                                                        valueTextType="TEXT"
                                                    />
                                                </div>
                                                */}
                                                <div className="grid-table-data col-start-1 col-span-1">
                                                    <CustomData
                                                        titleText={i18nUtils.get("", "Desde")}
                                                        titleTextType="TEXT"
                                                        valueText={tax.dateFrom}
                                                        valueTextType="TEXT"
                                                    />
                                                </div>

                                                <div className="grid-table-data col-start-2 col-span-1">
                                                    <CustomData
                                                        titleText={i18nUtils.get("", "Hasta")}
                                                        titleTextType="TEXT"
                                                        valueText={tax.dateTo}
                                                        valueTextType="TEXT"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                        <div className="grid-table-data col-start-2 col-span-5">
                                            <Field
                                                component={Uploader}
                                                idForm={FORM_ID}
                                                name="taxProfitRetentionDocumentId0"
                                                label={i18nUtils.get(
                                                    "",
                                                    "En caso de no coincidir, adjuntá  certificado de exclusión retención/percepción (opcional)",
                                                )}
                                                documentCode={thubanCodeGanancias}
                                                defaultDocument={{
                                                    documentId: taxDataBackup?.taxProfitRetentionDocumentId0,
                                                    documentName: taxDataBackup?.taxProfitRetentionDocumentId0Name,
                                                }}
                                                withDocumentName
                                            />
                                        </div>
                                        <div className="grid-table-data col-start-7 col-span-5">
                                            <Field
                                                component={Uploader}
                                                idForm={FORM_ID}
                                                name="taxProfitExemptionDocumentId0"
                                                label={i18nUtils.get("", "Adjuntá certificado de exento (opcional)")}
                                                documentCode={thubanCodeOtrosCertificados}
                                                defaultDocument={{
                                                    documentId: taxDataBackup?.taxProfitExemptionDocumentId0,
                                                    documentName: taxDataBackup?.taxProfitExemptionDocumentId0IdName,
                                                }}
                                                withDocumentName
                                            />
                                        </div>
                                    </div>
                                    <br />
                                    <div className="grid-table-row grid-cols-12">
                                        <div className="grid-table-data col-start-2 col-span-10">
                                            <ul className="list">
                                                <li>
                                                    <div className="grid-table-data col-start-2 col-span-5">
                                                        <I18n
                                                            id="onboarding.taxData.taxIIBB.label"
                                                            defaultValue="Impuestos de ingresos brutos (IIBB)"
                                                            component="b"
                                                        />
                                                    </div>

                                                    <>
                                                        <div className="grid-table-data col-span-12">
                                                            <Field
                                                                component={Selector}
                                                                renderAs="radio"
                                                                inLineControl
                                                                idForm={FORM_ID}
                                                                name="taxIIBBCategory"
                                                                options={[
                                                                    {
                                                                        id: "INSCRIPTO",
                                                                        label: i18nUtils.get("", "Inscripto"),
                                                                    },
                                                                    {
                                                                        id: "EXENTO",
                                                                        label: i18nUtils.get("", "Exento"),
                                                                    },
                                                                ]}
                                                                label={i18nUtils.get("", "Categoría")}
                                                                placeholder={i18nUtils.get(
                                                                    "onboarding.taxData.selectorPlaceholder",
                                                                    "Elegir una opción",
                                                                )}
                                                            />
                                                        </div>
                                                        <div className="documentation-container">
                                                            {allFormik?.values?.taxIIBBCategory === "INSCRIPTO" ? (
                                                                <>
                                                                    <div>
                                                                        <div className="grid-table-data col-span-6">
                                                                            <I18n
                                                                                id=""
                                                                                defaultValue={
                                                                                    allFormik?.values
                                                                                        ?.taxIIBBEnrolledCategory ===
                                                                                    "LOCAL"
                                                                                        ? "Convenio Local"
                                                                                        : "Convenio Multilateral"
                                                                                }
                                                                            />
                                                                        </div>
                                                                        <br className="conditional-br" />
                                                                        <br className="conditional-brx2" />
                                                                        <div className="grid-table-data col-span-6">
                                                                            <Field
                                                                                component={Uploader}
                                                                                idForm={FORM_ID}
                                                                                name="taxIIBBDocumentId"
                                                                                label={i18nUtils.get(
                                                                                    "",
                                                                                    "Adjuntá documentación",
                                                                                )}
                                                                                documentCode={
                                                                                    allFormik?.values
                                                                                        ?.taxIIBBEnrolledCategory ===
                                                                                    "LOCAL"
                                                                                        ? thubanCodeIIBBlocal
                                                                                        : thubanCodeIIBBMultilateral
                                                                                }
                                                                                defaultDocument={{
                                                                                    documentId:
                                                                                        taxDataBackup?.taxIIBBDocumentId,
                                                                                    documentName:
                                                                                        taxDataBackup?.taxIIBBDocumentIdName,
                                                                                }}
                                                                                withDocumentName
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <div className="grid-table-data col-span-6">
                                                                            <I18n
                                                                                id=""
                                                                                defaultValue={
                                                                                    allFormik?.values
                                                                                        ?.taxIIBBEnrolledCategory ===
                                                                                    "LOCAL"
                                                                                        ? "Otro certificado de no retención/percepción Local (opcional)"
                                                                                        : "Otro certificado de no retención/percepción Multilateral (opcional)"
                                                                                }
                                                                            />
                                                                        </div>
                                                                        <div className="grid-table-data col-span-6">
                                                                            <Field
                                                                                component={Uploader}
                                                                                idForm={FORM_ID}
                                                                                name="taxIIBBOtherDocumentId"
                                                                                label={i18nUtils.get(
                                                                                    "",
                                                                                    "Adjuntá documentación",
                                                                                )}
                                                                                documentCode={
                                                                                    allFormik?.values
                                                                                        ?.taxIIBBEnrolledCategory ===
                                                                                    "LOCAL"
                                                                                        ? thubanCodeOtrosCertificadosIIBBLocal
                                                                                        : thubanCodeOtrosCertificadosIIBBMult
                                                                                }
                                                                                defaultDocument={{
                                                                                    documentId:
                                                                                        taxDataBackup?.taxIIBBOtherDocumentId,
                                                                                    documentName:
                                                                                        taxDataBackup?.taxIIBBOtherDocumentIdName,
                                                                                }}
                                                                                withDocumentName
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div>
                                                                        <div className="grid-table-data col-span-6">
                                                                            <I18n
                                                                                id=""
                                                                                defaultValue="Certificado exento"
                                                                            />
                                                                        </div>
                                                                        <div className="grid-table-data col-span-6">
                                                                            <Field
                                                                                component={Uploader}
                                                                                idForm={FORM_ID}
                                                                                name="taxIIBBDocumentId"
                                                                                label={i18nUtils.get(
                                                                                    "",
                                                                                    "Adjuntá documentación",
                                                                                )}
                                                                                documentCode={thubanCodeIIBBExento}
                                                                                defaultDocument={{
                                                                                    documentId:
                                                                                        taxDataBackup?.taxIIBBDocumentId,
                                                                                    documentName:
                                                                                        taxDataBackup?.taxIIBBDocumentIdName,
                                                                                }}
                                                                                withDocumentName
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <div className="grid-table-data col-span-6">
                                                                            <I18n
                                                                                id=""
                                                                                defaultValue="Otros certificados IIBB Exento"
                                                                            />
                                                                        </div>
                                                                        <div className="grid-table-data col-span-6">
                                                                            <Field
                                                                                component={Uploader}
                                                                                idForm={FORM_ID}
                                                                                name="taxIIBBOtherDocumentId"
                                                                                label={i18nUtils.get(
                                                                                    "",
                                                                                    "Adjuntá documentación",
                                                                                )}
                                                                                documentCode={
                                                                                    thubanCodeOtrosCertificadosIIBBExento
                                                                                }
                                                                                defaultDocument={{
                                                                                    documentId:
                                                                                        taxDataBackup?.taxIIBBOtherDocumentId,
                                                                                    documentName:
                                                                                        taxDataBackup?.taxIIBBOtherDocumentIdName,
                                                                                }}
                                                                                withDocumentName
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                        {allFormik?.values?.taxIIBBCategory === "INSCRIPTO" && (
                                                            <div className="grid-table-data col-span-12">
                                                                <Field
                                                                    component={Selector}
                                                                    renderAs="radio"
                                                                    inLineControl
                                                                    idForm={FORM_ID}
                                                                    name="taxIIBBEnrolledCategory"
                                                                    options={[
                                                                        {
                                                                            id: "LOCAL",
                                                                            label: i18nUtils.get("", "Local"),
                                                                        },
                                                                        {
                                                                            id: "MULTILATERAL",
                                                                            label: i18nUtils.get("", "Multilateral"),
                                                                        },
                                                                    ]}
                                                                    label={i18nUtils.get("", "Tipo de convenio")}
                                                                    placeholder={i18nUtils.get(
                                                                        "onboarding.taxData.selectorPlaceholder",
                                                                        "Elegir una opción",
                                                                    )}
                                                                />
                                                            </div>
                                                        )}
                                                    </>
                                                </li>
                                                <li>
                                                    <div className="grid-table-data col-span-6 pb-4">
                                                        <Field
                                                            component={Checkbox}
                                                            idForm={FORM_ID}
                                                            name="taxStamp"
                                                            labelText="onboarding.taxData.taxStamp.label"
                                                            labelTextProps={{
                                                                defaultValue: "Exento en impuestos al sello",
                                                            }}
                                                        />
                                                    </div>
                                                    {!!allFormik?.values?.taxStamp && (
                                                        <div className="grid-table-data col-span-12">
                                                            <Field
                                                                component={Uploader}
                                                                idForm={FORM_ID}
                                                                name="taxStampDocumentId"
                                                                label={i18nUtils.get("", "Adjuntá documentación")}
                                                                documentCode={thubanCodeExentoImpSello}
                                                                defaultDocument={{
                                                                    documentId: taxDataBackup?.taxStampDocumentId,
                                                                    documentName: taxDataBackup?.taxStampDocumentIdName,
                                                                }}
                                                                withDocumentName
                                                            />
                                                        </div>
                                                    )}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div className="grid-table form-actions">
                                    <div className="grid-table-row sm:flex md:grid-cols-12">
                                        {/* // White card */}
                                        <div className="grid-table-data md:col-start-5 md:col-span-4">
                                            <Button
                                                bsStyle="primary"
                                                label="global.continue"
                                                loading={isLoading}
                                                type="submit"
                                                block
                                                disabled={!submitIsAllowed(allFormik)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </MainContainer>

            <EnvironmentHelpSlide
                slideIsVisible={helpIsVisible}
                closeSlide={() => setHelpIsVisible(false)}
                isMobile={isMobile}
                page={PAGE_ID}
            />

            <ErrorPopup error={error} handleClick={() => dispatch(modalActions.modalClose())} />
        </>
    );
};

StepTaxData.propTypes = {
    isMobile: bool.isRequired,
    taxDataBackup: arrayOf(shape({})).isRequired,
    taxDataRequest: arrayOf(shape({})).isRequired,
    taxSelectorRequest: arrayOf(shape({})).isRequired,
};

const mapStateToProps = (state) => ({
    taxDataBackup: environmentSelectors.getTaxDataBackup(state),
    taxDataRequest: environmentSelectors.getTaxRequest(state),
});

export default connect(mapStateToProps)(StepTaxData);
